import React, { Component } from 'react';
import { connect } from 'react-redux';
import { AutoSizer, InfiniteLoader, List } from 'react-virtualized';

import { UserlistSection } from 'models/UserlistSection.jsx';
import UserUserlistComponent from 'react/chat/userlist/UserUserlistComponent.jsx';

import UserlistHeaderSection from './UserlistHeaderSection';
import LoaderFullPage from '../../elements/LoaderFullPage';
import UserlistSkeletonRow from 'react/chat/userlist/UserlistSkeletonRow';


class UserlistComponent extends Component {

	static defaultProps = {
		useAutosize: true,
	};

	constructor(props) {
		super(props);
		this.ref = React.createRef();
		this.unmounting = false;

		this.rowRenderer = this.rowRenderer.bind(this);
		this.sectionClick = this.sectionClick.bind(this);

		this.isRowLoaded = this.isRowLoaded.bind(this);
		this.getScrollParent = this.getScrollParent.bind(this);

		this.currentSectionList = [];
	}

	componentDidMount() {
		const { userlist } = this.props;
		if(!userlist) {
			return;
		}
		userlist.retrieveUserlist()
			.then((response) => {
				if(this.unmounting) {
					return;
				}
				this.forceUpdate();
			});
	}

	componentWillUnmount() {
		this.unmounting = true;
	}

	getScrollParent() {
		return this.ref.current;
	}

	sectionClick(section, e) {
		e.stopPropagation();
		section.toggle()
			.then(() => {
				this.forceUpdate();
			});
		this.forceUpdate();
	}

	resetSections() {
		const { userlist } = this.props;
		this.sectionUsers = {};
		if(!userlist.sections) {
			return;
		}
		for(let section of userlist.sections) {
			this.sectionUsers[section.name] = {};
		}
	}

	rowRenderer({
		            index,       // Index of row
		            style        // Style object to be applied to row (to position it);
	            }) {
		const { onUserClick, userlist, renderWith, renderSectionWith, renderVersion, ...props } = this.props;
		const entry = this.currentSectionList[index];
		if(!entry) {
			return <UserlistSkeletonRow key={`skeleton-${index}`} style={style} />;
		}
		if(entry instanceof UserlistSection) {
			const section = entry;
			if(renderSectionWith) {
				return renderSectionWith({ section, style, sectionClick: this.sectionClick });
			}
			return (
				<UserlistHeaderSection
					key={section.name}
					section={section}
					style={style}
					loading={section.loading}
					sectionClick={this.sectionClick}
				/>

			);
		}
		else {
			const user = entry;
			if(renderWith) {
				return renderWith({
					user,
					props: {
						style
					},
				});
			}
			return (
				<UserUserlistComponent
					onClick={onUserClick}
					key={user.id}
					style={style}
					user={user}
					{...props}
					friendRequest={user.friend}
					isChatMod={user.chat_rooms.isModOrAdmin(userlist.chatRoomId)}
				/>
			);
		}
	}

	isRowLoaded({ index }) {
		const { infiniteScrollProps } = this.props;
		if(!infiniteScrollProps || !infiniteScrollProps.hasMore) {
			return true;
		}
		return !!this.currentSectionList[index];
	}

	render() {
		const { userlist, useAutosize, infiniteScrollProps, isLoading, renderVersion } = this.props;
		const { displayedPrivateMessages, currentPrivateChat, unreadPrivateMessageCount, activeToolTips } = this.props;
		if(!userlist) {
			return null;
		}
		const sections = userlist.retrieveSections();
		this.resetSections();
		this.currentSectionList = [];
		for(let section of sections) {
			if(!section.showWhenEmpty && section.getCount() === 0) {
				continue;
			}
			const users = section.users;
			if(_.size(sections) !== 1) {
				this.currentSectionList.push(section);
			}

			for(let [_, user, renderWithProps] of users) {
				this.sectionUsers[user.id] = section;
				if(section.toggledOn) {
					this.currentSectionList.push(user);
				}
			}
		}

		const hasElements = !isLoading && this.currentSectionList.length > 0;
		// Rows the list knows are coming but hasn't loaded, drawn as skeletons.
		const expectedRowCount = (infiniteScrollProps && infiniteScrollProps.hasMore && infiniteScrollProps.expectedRowCount) || 0;
		const rowCount = Math.max(this.currentSectionList.length, expectedRowCount);

		let infiniteScrollPropsToUse = null;
		let listExtraProps = {};
		if(infiniteScrollProps) {
			infiniteScrollPropsToUse = {};
			infiniteScrollPropsToUse.loadMoreRows = infiniteScrollProps.loadMore;
			infiniteScrollPropsToUse.isRowLoaded = this.isRowLoaded;
			if(expectedRowCount) {
				infiniteScrollPropsToUse.rowCount = rowCount;
			}
			else {
				infiniteScrollPropsToUse.rowCount = infiniteScrollProps.hasMore ? 20000 : this.currentSectionList.length;
			}
			listExtraProps.data2 = displayedPrivateMessages;
			listExtraProps.data3 = currentPrivateChat;
			listExtraProps.data4 = unreadPrivateMessageCount;
			listExtraProps.data5 = activeToolTips;
		}

		return (
			<div
				className='list_container chat_room_user_list active'>
				{hasElements &&
				<React.Fragment>
					{useAutosize &&
					<React.Fragment>
						{infiniteScrollPropsToUse &&
						<InfiniteLoader
							{...infiniteScrollPropsToUse}
						>
							{(infiniteLoaderProps) => (
								<AutoSizer>
									{({ width, height }) => (
										<List
											ref={infiniteLoaderProps.registerChild}
											onRowsRendered={infiniteLoaderProps.onRowsRendered}

											width={width}
											height={height}
											rowCount={rowCount}
											rowHeight={43}
											rowRenderer={this.rowRenderer}
											{...listExtraProps}
										/>
									)}
								</AutoSizer>
							)}
						</InfiniteLoader>
						}
						{!infiniteScrollPropsToUse &&
						<AutoSizer>
							{({ width, height }) => (
								<List
									width={width}
									height={height}
									rowCount={this.currentSectionList.length}
									rowHeight={43}
									rowRenderer={this.rowRenderer}
									renderVersion={renderVersion}
								/>
							)}
						</AutoSizer>
						}
					</React.Fragment>
					}
					{!useAutosize &&
					<React.Fragment>
						{this.currentSectionList.map((entry, index) => {
							return this.rowRenderer({ index, style: {} });
						})}
					</React.Fragment>
					}
				</React.Fragment>
				}
				{!hasElements &&
				<LoaderFullPage />
				}
			</div>
		);
	}

}

function mapStateToProps(state) {
	return {
		...state.chat,
		...state.privateChat,
	};
}

export default connect(
	mapStateToProps,
)(UserlistComponent);